import { useCallback, useMemo, useRef, useState } from "react";

const shallowEqual = <T extends Record<string, unknown>>(a: T, b: T): boolean => {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const key of keys) {
		if (a[key] !== b[key]) return false;
	}
	return true;
};

const diffShallow = <T extends Record<string, unknown>>(snapshot: T, current: T): Partial<T> => {
	const out: Partial<T> = {};
	const keys = new Set([...Object.keys(snapshot), ...Object.keys(current)]);
	for (const key of keys) {
		if (snapshot[key as keyof T] !== current[key as keyof T]) {
			out[key as keyof T] = current[key as keyof T];
		}
	}
	return out;
};

export const useDirtyForm = <T extends Record<string, unknown>>(initial: T) => {
	// Snapshot in state drives isDirty/diff memo reactivity; ref keeps callbacks
	// stable so caller useEffects with [callback] deps don't loop after syncFromServer.
	const [snapshot, setSnapshot] = useState<T>(initial);
	const [values, setValues] = useState<T>(initial);
	const snapshotRef = useRef<T>(initial);
	snapshotRef.current = snapshot;

	const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
		setValues((prev) => ({ ...prev, [key]: value }));
	}, []);

	const reset = useCallback((next?: T) => {
		const target = next ?? snapshotRef.current;
		setSnapshot(target);
		setValues(target);
	}, []);

	const discard = useCallback(() => {
		setValues(snapshotRef.current);
	}, []);

	// Advances snapshot to new server values; keeps in-progress edits when dirty.
	const syncFromServer = useCallback((next: T) => {
		setSnapshot(next);
		setValues((current) => (shallowEqual(current, snapshotRef.current) ? next : current));
	}, []);

	const isDirty = useMemo(() => !shallowEqual(values, snapshot), [values, snapshot]);

	const diff = useMemo(() => diffShallow(snapshot, values), [snapshot, values]);

	return { values, setField, reset, discard, syncFromServer, isDirty, diff };
};
